import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { FiFileText, FiCheck, FiAlertTriangle, FiRefreshCw } from "react-icons/fi";
import type { LabelledSliderRange, SliderRange, ValuationPreset, ValuationSimulatorConfig } from "@dd-report-components/report/ExecutiveSummaryCockpit";
import { useSimulatorText } from "./simulatorText";

// ── Layout ─────────────────────────────────────────────────────────
const SimulatorSection = styled.div`
  margin: 1.75rem 0 1.25rem;
`;

const SimulatorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 0.5rem;
`;

const PanelIconBadge = styled.span`
  flex-shrink: 0;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.56rem;
  background: #eaf5ef;
  color: #2c8c5e;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #14323b;
`;

const IntroCaption = styled.div`
  font-size: 0.81rem;
  color: #8a97a0;
  margin-bottom: 0.75rem;
  max-width: 44rem;
  line-height: 1.55;

  strong {
    color: #5a6c72;
    font-weight: 700;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.1rem;
`;

const ApplicableChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.7rem;
  background: #eaf5ef;
  border: 1px solid #d4eadd;
  border-radius: 100px;
  font-size: 0.66rem;
  font-weight: 700;
  color: #237a50;
`;

const ChipSpacer = styled.span`
  flex: 1;
  min-width: 0.75rem;
`;

const ScenarioLabel = styled.span`
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa7ac;
`;

const PresetButton = styled.button<{ $active: boolean }>`
  padding: 0.375rem 0.94rem;
  border-radius: 100px;
  border: 1px solid ${(p) => (p.$active ? "#14323b" : "#e4eae7")};
  background: ${(p) => (p.$active ? "#14323b" : "#ffffff")};
  color: ${(p) => (p.$active ? "#ffffff" : "#5a6c72")};
  font-family: inherit;
  font-size: 0.69rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

const SimulatorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  align-items: stretch;
`;

const SectionKicker = styled.div`
  font-size: 0.59rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9aa7ac;
`;

// ── Left panel: inputs ─────────────────────────────────────────────
const InputPanel = styled.div`
  background: #fafbfa;
  border: 1px solid #ecefed;
  border-radius: 1rem;
  padding: 1.1rem 1.5rem 1.375rem;
  display: flex;
  flex-direction: column;
  gap: 1.375rem;
`;

const Field = styled.div``;

const FieldHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.625rem;
`;

const FieldLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.78rem;
  color: #5a6c72;
`;

const FieldValue = styled.span`
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #14323b;
  white-space: nowrap;
`;

const FieldCaption = styled.span<{ $pending?: boolean }>`
  font-size: 0.625rem;
  color: ${({ $pending }) => ($pending ? "#c9820e" : "#a2afb4")};
  font-weight: ${({ $pending }) => ($pending ? 600 : 400)};
  white-space: nowrap;
`;

// A report's own caveat under the scenario chips (how its presets were
// calibrated), and the reminder under the charges while they are still zero.
const AmberNote = styled.div`
  font-size: 0.72rem;
  color: #8a6414;
  line-height: 1.5;
`;

const CalibrationNote = styled(AmberNote)`
  margin: -0.4rem 0 1rem;
  max-width: 47.5rem;
`;

const PendingNote = styled(AmberNote)`
  margin-top: 0.5rem;
  font-size: 0.69rem;
`;

const InfoTip = styled.span`
  cursor: help;
  flex-shrink: 0;
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  border: 1px solid #c4cfc9;
  color: #8a97a0;
  font-size: 0.56rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:hover > span,
  &:focus > span {
    display: block;
  }
`;

const InfoBubble = styled.span`
  display: none;
  position: absolute;
  bottom: 1.4rem;
  left: 50%;
  transform: translateX(-50%);
  width: 15rem;
  background: #14323b;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 400;
  line-height: 1.4;
  padding: 0.6rem 0.7rem;
  border-radius: 0.5rem;
  z-index: 10;
  cursor: default;
  white-space: normal;
`;

const NumberInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #14323b;
  border: 1px solid #e4eae7;
  border-radius: 0.56rem;
  padding: 0.56rem 0.75rem;
  background: #ffffff;
  margin-top: 0.56rem;

  &::placeholder {
    font-weight: 400;
    font-size: 0.72rem;
    color: #a2afb4;
  }
`;

const RangeInput = styled.input`
  width: 100%;
  accent-color: #35a46e;
  height: 4px;
  cursor: pointer;
  margin-top: 0.7rem;
`;

const SliderEndLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.625rem;
  color: #b0bcb6;
  margin-top: 0.3rem;
`;

const SyncBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.7rem;
  padding: 0.56rem 0.69rem;
  background: #eaf5ef;
  border: 1px solid #d4eadd;
  border-radius: 0.625rem;

  svg {
    flex-shrink: 0;
    color: #237a50;
  }
`;

const SyncBannerText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 0.69rem;
  color: #237a50;
  line-height: 1.4;

  b {
    font-weight: 700;
  }
`;

const SyncEditButton = styled.button`
  flex-shrink: 0;
  padding: 0.3rem 0.69rem;
  border-radius: 0.5rem;
  border: 1px solid #cad8d0;
  background: #ffffff;
  color: #237a50;
  font-family: inherit;
  font-size: 0.66rem;
  font-weight: 700;
  cursor: pointer;
`;

const ResyncRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.625rem;
  color: #b0bcb6;
  margin-top: 0.3rem;
`;

const ResyncButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.19rem 0.56rem;
  border-radius: 0.44rem;
  border: 1px solid #e4eae7;
  background: #ffffff;
  color: #5a6c72;
  font-family: inherit;
  font-size: 0.625rem;
  font-weight: 700;
  cursor: pointer;
`;

const InputDivider = styled.div`
  padding-top: 1rem;
  border-top: 1px solid #eef1ef;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

// ── Right panel: waterfall ─────────────────────────────────────────
const ResultPanel = styled.div`
  background: #ffffff;
  border: 1px solid #eef1ef;
  border-radius: 1rem;
  padding: 1.1rem 1.5rem 1.375rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`;

const Waterfall = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.69rem;
`;

const WaterfallRow = styled.div``;

const WaterfallLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  margin-bottom: 0.3rem;
`;

const WaterfallLabel = styled.span<{ $total?: boolean }>`
  color: ${(p) => (p.$total ? "#22343b" : "#5a6c72")};
  font-weight: ${(p) => (p.$total ? 600 : 400)};
`;

const WaterfallValue = styled.span<{ $color?: string }>`
  font-family: inherit;
  font-weight: 700;
  color: ${(p) => p.$color ?? "#55666c"};
`;

const WaterfallTrack = styled.div<{ $height?: string }>`
  height: ${(p) => p.$height ?? "0.5rem"};
  border-radius: 0.3rem;
  background: #eef1ef;
  overflow: hidden;
`;

const WaterfallFill = styled.div<{ $width: string; $color: string }>`
  height: 100%;
  width: ${(p) => p.$width};
  background: ${(p) => p.$color};
  border-radius: 0.3rem;
`;

const ResultStatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
  margin-top: 0.125rem;
`;

const ResultStatBox = styled.div<{ $accent?: boolean }>`
  background: ${(p) => (p.$accent ? "#f1f8f4" : "#fafbfa")};
  border: 1px solid ${(p) => (p.$accent ? "#deeee5" : "#ecefed")};
  border-radius: 0.75rem;
  padding: 0.81rem 0.94rem;
`;

const ResultStatLabel = styled.div<{ $accent?: boolean }>`
  font-size: 0.53rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(p) => (p.$accent ? "#237a50" : "#9aa7ac")};
  line-height: 1.3;
`;

const ResultStatValue = styled.div<{ $color?: string }>`
  margin-top: 0.44rem;
  font-family: inherit;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${(p) => p.$color ?? "#14323b"};
  line-height: 1;
`;

const ResultStatSub = styled.div`
  margin-top: 0.25rem;
  font-size: 0.66rem;
  color: #7c8b91;
`;

const ScenarioBandKicker = styled.div`
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #9aa7ac;
  margin-bottom: 0.5rem;
`;

const ScenarioBandGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
`;

const ScenarioBandBox = styled.div<{ $central?: boolean }>`
  background: ${(p) => (p.$central ? "#f1f8f4" : "#fafbfa")};
  border: 1px solid ${(p) => (p.$central ? "#deeee5" : "#ecefed")};
  border-radius: 0.69rem;
  padding: 0.69rem 0.625rem;
  text-align: center;
`;

const ScenarioBandLabel = styled.div<{ $color: string }>`
  font-size: 0.56rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(p) => p.$color};
`;

const ScenarioBandValue = styled.div`
  margin-top: 0.3rem;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 800;
  color: #14323b;
`;

const ScenarioBandSub = styled.div`
  margin-top: 0.125rem;
  font-size: 0.59rem;
  color: #9aa7ac;
`;

const WarningBox = styled.div`
  display: flex;
  gap: 0.625rem;
  padding: 0.75rem 0.875rem;
  background: #fbeee8;
  border: 1px solid #f2d9ce;
  border-radius: 0.75rem;

  svg {
    flex-shrink: 0;
    margin-top: 0.0625rem;
    color: #c0392b;
  }
`;

const WarningText = styled.span`
  font-size: 0.75rem;
  color: #8a4b3a;
  line-height: 1.5;
`;

const MarginBox = styled.div<{ $bg: string; $border: string }>`
  margin-top: auto;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};
`;

const MarginHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const MarginKicker = styled.span<{ $color: string }>`
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${(p) => p.$color};
  max-width: 14rem;
  line-height: 1.35;
`;

const MarginFigures = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: baseline;
  gap: 0.5rem;
  font-family: inherit;
  color: ${(p) => p.$color};

  b {
    font-size: 1.125rem;
    font-weight: 800;
  }
  i {
    font-style: normal;
    font-size: 0.81rem;
    font-weight: 700;
  }
`;

const MarginVerdict = styled.div`
  margin-top: 0.44rem;
  font-size: 0.75rem;
  line-height: 1.5;
  color: #5a6c72;
`;

const MarginFootnote = styled.div`
  margin-top: 0.56rem;
  padding-top: 0.56rem;
  border-top: 1px solid rgba(20, 50, 59, 0.08);
  font-size: 0.69rem;
  color: #7c8b91;
  line-height: 1.5;

  strong {
    color: #37474d;
    font-weight: 700;
  }
`;

const NoCompraBox = styled.div`
  margin-top: auto;
  padding: 1rem;
  border-radius: 0.75rem;
  background: #fafbfa;
  border: 1px dashed #d8dedb;
  font-size: 0.75rem;
  color: #8a97a0;
  line-height: 1.5;
  text-align: center;
`;

const Disclaimer = styled.div`
  margin-top: 0.75rem;
  font-size: 0.66rem;
  color: #a2afb4;
  line-height: 1.55;
`;

interface Props {
  // Buildable area (m²t) linked from the housing simulator, when synced.
  linkedFloorArea?: number;
  // Whether that simulator has turned a commercial ground floor into housing.
  linkedGroundFloorIsHousing?: boolean;
  plotAreaM2?: number;
  // The property's own market; absent, the residential defaults below.
  config?: ValuationSimulatorConfig;
}

type PresetKey = "pesimista" | "central" | "optimista";
type Ranges = NonNullable<ValuationSimulatorConfig["ranges"]>;

// The residential plot the simulator was first drawn with (Torrent): what a
// report that only says `valuationSimulator: true` gets.
const DEFAULT_PRESETS: Record<PresetKey, ValuationPreset> = {
  pesimista: { venta: 1600, coste: 1300, benef: 20, soft: 11 },
  central: { venta: 1675, coste: 1250, benef: 18, soft: 9 },
  optimista: { venta: 1850, coste: 1200, benef: 15, soft: 7 },
};

const DEFAULT_RANGES: { floorArea: SliderRange; venta: SliderRange; coste: SliderRange } = {
  floorArea: { min: 4000, max: 5800, step: 25 },
  venta: { min: 1400, max: 2400, step: 25 },
  coste: { min: 1000, max: 1700, step: 25 },
};

const DEFAULT_MARGIN_RANGES: { benef: SliderRange; soft: SliderRange } = {
  benef: { min: 10, max: 25, step: 1 },
  soft: { min: 0, max: 20, step: 1 },
};

const DEFAULT_FLOOR_AREA = 5030;

const DEFAULT_ACQUISITION_TAX_PCT = 1.4; // AJD

const DEFAULT_PLOT_AREA_M2 = 1630; // Seed plot surface used when the report carries no usable one.

const fmtE = (n: number) => Math.round(n).toLocaleString("es-ES");

// Slider end labels always group thousands ("1.400"); es-ES leaves a
// four-digit figure ungrouped.
const fmtLabel = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

const fmtMn = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1e6) {
    return (abs / 1e6).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " M€";
  }
  return fmtE(Math.round(abs / 1000) * 1000) + " €";
};

const fmtM = (n: number) => (n < 0 ? "−" : "") + "≈" + fmtMn(n);

const clampPct = (x: number) => Math.max(0, Math.min(100, x)).toFixed(1) + "%";

// Client-side calculator — static residual method (Orden ECO/805/2003, art.
// 42), same math as the design's reference implementation (Rv/Rlow/Rhigh/
// ofert/colchon in Rediseno_DD_v5.dc.html). The report only supplies the
// starting assumptions (`config`) and, when synced, the housing simulator's
// buildable area; every figure is computed here.
export const ValuationSimulator = ({ linkedFloorArea, linkedGroundFloorIsHousing, plotAreaM2 = DEFAULT_PLOT_AREA_M2, config }: Props) => {
  const { i18n } = useTranslation();
  const { text, rich, has } = useSimulatorText("dueDiligence.finalReport.cockpit.valuationSimulator", config?.texts);
  // A plot surface of 0 divides as badly as a missing one, and the parameter
  // default above only catches `undefined`.
  const plotArea = plotAreaM2 > 0 ? plotAreaM2 : DEFAULT_PLOT_AREA_M2;
  const PRESETS = config?.presets ?? DEFAULT_PRESETS;
  const floorAreaRange: Ranges["floorArea"] & SliderRange = config?.ranges?.floorArea ?? DEFAULT_RANGES.floorArea;
  const ventaRange: LabelledSliderRange = config?.ranges?.venta ?? DEFAULT_RANGES.venta;
  const costeRange: LabelledSliderRange = config?.ranges?.coste ?? DEFAULT_RANGES.coste;
  const benefRange: LabelledSliderRange = config?.ranges?.benef ?? DEFAULT_MARGIN_RANGES.benef;
  const softRange: LabelledSliderRange = config?.ranges?.soft ?? DEFAULT_MARGIN_RANGES.soft;
  const floorAreaUnit = config?.units?.floorArea ?? "m²t";
  const costeUnit = config?.units?.coste ?? "€/m²t";
  const taxPct = config?.acquisitionTaxPct ?? DEFAULT_ACQUISITION_TAX_PCT;
  const TADQ = taxPct / 100;
  const canLink = linkedFloorArea != null;
  // Only start synced when the housing simulator is actually there to feed us
  // a buildable area; otherwise the manual input drives the calculation.
  const [linked, setLinked] = useState(canLink && !config?.startUnlinked);
  const [manualEdif, setManualEdif] = useState(config?.initialFloorArea ?? DEFAULT_FLOOR_AREA);
  const [venta, setVenta] = useState(PRESETS.central.venta);
  const [coste, setCoste] = useState(PRESETS.central.coste);
  const [benef, setBenef] = useState(PRESETS.central.benef);
  const [soft, setSoft] = useState(PRESETS.central.soft);
  const [cargas, setCargas] = useState(config?.initialCharges ?? 0);
  const guaranteeFee = config?.guaranteeFee;
  const [aval, setAval] = useState(guaranteeFee?.initial ?? 0);
  // Everything the residual subtracts below the margin: the charges, plus the
  // bond's fee when the report has one.
  const deductions = cargas + (guaranteeFee ? aval : 0);
  const chargesPending = !!config?.chargesPending;
  const [compra, setCompra] = useState<number | null>(null);

  // Only a manual edit can leave the ground floor commercial; synced, the
  // housing simulator says what it is.
  const hasCommercial = linked ? !linkedGroundFloorIsHousing : true;
  const edifMax = hasCommercial ? floorAreaRange.max : (floorAreaRange.maxWithoutCommercial ?? floorAreaRange.max);
  const linkedEdif = Math.round(linkedFloorArea ?? 0);
  // A report that says where the cap sits without the shop also caps the
  // synced area there, as the design does.
  const edif = linked ? (floorAreaRange.maxWithoutCommercial != null ? Math.min(linkedEdif, edifMax) : linkedEdif) : manualEdif;
  const fixedRevenue = (config?.fixedRevenue?.amount ?? 0) + (hasCommercial ? config?.fixedRevenue?.groundFloorCommercial ?? 0 : 0);

  const resid = (v: number, c: number, b: number) => (v * edif + fixedRevenue) * (1 - (b + soft) / 100) - c * edif - deductions;
  const Rv = resid(venta, coste, benef);
  const band = config?.priceBand;
  const Rlow = band ? resid(band.low, coste, benef) : resid(venta * 0.9, coste * 1.1, benef * 1.1);
  const Rhigh = band ? resid(band.high, coste, benef) : resid(venta * 1.1, coste * 0.9, benef * 0.9);
  const ofert = Rv / (1 + TADQ);
  // The band's own prices, for a report's wording of its labels.
  const bandVars = band ? { low: fmtLabel(band.low), high: fmtLabel(band.high) } : undefined;

  const ingresos = venta * edif + fixedRevenue;
  const cConstr = coste * edif;
  const cMargen = (ingresos * (benef + soft)) / 100;
  const hasCargasLine = deductions > 0;
  const sMax = Math.max(ingresos, 1);
  const rep = Rv / plotArea;
  const Rpos = Rv >= 0;

  const hasCompra = !!compra && compra > 0;
  const colchon = hasCompra ? ofert - (compra as number) : 0;
  const colPct = hasCompra ? (colchon / (compra as number)) * 100 : 0;
  const colPos = colchon >= 0;
  const keptShare = 1 - (benef + soft) / 100;
  const venBE = hasCompra && edif > 0 && keptShare > 0 ? ((compra! * (1 + TADQ) + cConstr + deductions) / keptShare - fixedRevenue) / edif : 0;

  const activePreset = (Object.keys(PRESETS) as PresetKey[]).find(
    (k) => PRESETS[k].venta === venta && PRESETS[k].coste === coste && PRESETS[k].benef === benef && PRESETS[k].soft === soft
  );

  const applyPreset = (key: PresetKey) => {
    const p = PRESETS[key];
    setVenta(p.venta);
    setCoste(p.coste);
    setBenef(p.benef);
    setSoft(p.soft);
  };

  return (
    <SimulatorSection>
      <SimulatorHeader>
        <PanelIconBadge>
          <FiFileText size={15} />
        </PanelIconBadge>
        <PanelTitle>{text("title")}</PanelTitle>
      </SimulatorHeader>
      <IntroCaption>
        {rich("intro", undefined, { 1: <strong /> })}
      </IntroCaption>

      <ChipRow>
        <ApplicableChip>
          <FiCheck size={12} />
          {text("applicableChip")}
        </ApplicableChip>
        <ChipSpacer />
        <ScenarioLabel>{text("scenarioLabel")}</ScenarioLabel>
        {(Object.keys(PRESETS) as PresetKey[]).map((key) => (
          <PresetButton key={key} type="button" $active={activePreset === key} onClick={() => applyPreset(key)}>
            {text(`presets.${key}`)}
          </PresetButton>
        ))}
      </ChipRow>
      {has("calibrationNote") && <CalibrationNote>{text("calibrationNote")}</CalibrationNote>}

      <SimulatorGrid>
        <InputPanel>
          <SectionKicker>{text("paramsTitle")}</SectionKicker>

          <Field>
            <FieldHeader>
              <FieldLabel>
                {text("edifLabel")}
                <InfoTip tabIndex={0}>
                  i<InfoBubble>{config?.tips?.floorArea ?? text("edifTip")}</InfoBubble>
                </InfoTip>
              </FieldLabel>
              <FieldValue>{fmtE(edif)} {floorAreaUnit}</FieldValue>
            </FieldHeader>

            {linked ? (
              <SyncBanner>
                <FiCheck size={13} />
                <SyncBannerText>
                  {rich("syncedText", { value: fmtE(linkedEdif) }, { 1: <b /> })}
                </SyncBannerText>
                <SyncEditButton type="button" onClick={() => setLinked(false)}>
                  {text("editByHand")}
                </SyncEditButton>
              </SyncBanner>
            ) : (
              <>
                <RangeInput
                  type="range"
                  min={floorAreaRange.min}
                  max={edifMax}
                  step={floorAreaRange.step}
                  value={manualEdif}
                  onChange={(e) => setManualEdif(+e.target.value)}
                />
                <ResyncRow>
                  <span>{floorAreaRange.minLabel ?? fmtLabel(floorAreaRange.min)}</span>
                  {/* With no housing simulator there is nothing to resync with:
                      resyncing would pin the floor area to zero. */}
                  {canLink ? (
                    <ResyncButton type="button" onClick={() => setLinked(true)}>
                      <FiRefreshCw size={11} />
                      {text("resync")}
                    </ResyncButton>
                  ) : (
                    config?.ranges?.floorArea?.hint && <span>{config.ranges.floorArea.hint}</span>
                  )}
                  <span>{floorAreaRange.maxLabel ?? `${fmtLabel(edifMax)} ${floorAreaUnit}`}</span>
                </ResyncRow>
              </>
            )}
          </Field>

          <Field>
            <FieldHeader>
              <FieldLabel>
                {text("ventaLabel")}
                <InfoTip tabIndex={0}>
                  i<InfoBubble>{config?.tips?.venta ?? text("ventaTip")}</InfoBubble>
                </InfoTip>
              </FieldLabel>
              <FieldValue>{fmtE(venta)} €/m²</FieldValue>
            </FieldHeader>
            <RangeInput type="range" min={ventaRange.min} max={ventaRange.max} step={ventaRange.step} value={venta} onChange={(e) => setVenta(+e.target.value)} />
            <SliderEndLabels>
              <span>{ventaRange.minLabel ?? fmtLabel(ventaRange.min)}</span>
              <span>{ventaRange.maxLabel ?? `${fmtLabel(ventaRange.max)} €/m²`}</span>
            </SliderEndLabels>
          </Field>

          <Field>
            <FieldHeader>
              <FieldLabel>
                {text("costeLabel")}
                <InfoTip tabIndex={0}>
                  i<InfoBubble>{config?.tips?.coste ?? text("costeTip")}</InfoBubble>
                </InfoTip>
              </FieldLabel>
              <FieldValue>{fmtE(coste)} {costeUnit}</FieldValue>
            </FieldHeader>
            <RangeInput type="range" min={costeRange.min} max={costeRange.max} step={costeRange.step} value={coste} onChange={(e) => setCoste(+e.target.value)} />
            <SliderEndLabels>
              <span>{costeRange.minLabel ?? fmtLabel(costeRange.min)}</span>
              <span>{costeRange.maxLabel ?? `${fmtLabel(costeRange.max)} ${costeUnit}`}</span>
            </SliderEndLabels>
          </Field>

          <Field>
            <FieldHeader>
              <FieldLabel>
                {text("benefLabel")}
                <InfoTip tabIndex={0}>
                  i<InfoBubble>{config?.tips?.benef ?? text("benefTip")}</InfoBubble>
                </InfoTip>
              </FieldLabel>
              <FieldValue>{benef} %</FieldValue>
            </FieldHeader>
            <RangeInput type="range" min={benefRange.min} max={benefRange.max} step={benefRange.step} value={benef} onChange={(e) => setBenef(+e.target.value)} />
            <SliderEndLabels>
              <span>{benefRange.minLabel ?? `${benefRange.min}%`}</span>
              <span>{benefRange.maxLabel ?? `${benefRange.max}% s/ventas`}</span>
            </SliderEndLabels>
          </Field>

          <Field>
            <FieldHeader>
              <FieldLabel>
                {text("softLabel")}
                <InfoTip tabIndex={0}>
                  i<InfoBubble>{config?.tips?.soft ?? text("softTip")}</InfoBubble>
                </InfoTip>
              </FieldLabel>
              <FieldValue>{soft} %</FieldValue>
            </FieldHeader>
            <RangeInput type="range" min={softRange.min} max={softRange.max} step={softRange.step} value={soft} onChange={(e) => setSoft(+e.target.value)} />
            <SliderEndLabels>
              <span>{softRange.minLabel ?? `${softRange.min}%`}</span>
              <span>{softRange.maxLabel ?? `${softRange.max}% s/ventas`}</span>
            </SliderEndLabels>
          </Field>

          <InputDivider>
            <Field>
              <FieldHeader>
                <FieldLabel>
                  {text("cargasLabel")}
                  <InfoTip tabIndex={0}>
                    i<InfoBubble>{config?.tips?.cargas ?? text("cargasTip")}</InfoBubble>
                  </InfoTip>
                </FieldLabel>
                <FieldCaption $pending={chargesPending}>{text(chargesPending ? "pendingCaption" : "cargasCaption")}</FieldCaption>
              </FieldHeader>
              <NumberInput type="number" step={10000} value={cargas} onChange={(e) => setCargas(+e.target.value || 0)} />
            </Field>

            {guaranteeFee && (
              <Field>
                <FieldHeader>
                  <FieldLabel>
                    {text("avalLabel")}
                    <InfoTip tabIndex={0}>
                      i<InfoBubble>{config?.tips?.aval ?? text("avalTip")}</InfoBubble>
                    </InfoTip>
                  </FieldLabel>
                  <FieldCaption $pending={chargesPending}>{text(chargesPending ? "pendingCaption" : "avalCaption")}</FieldCaption>
                </FieldHeader>
                <NumberInput type="number" step={1000} value={aval} onChange={(e) => setAval(+e.target.value || 0)} />
                {chargesPending && (cargas === 0 || aval === 0) && <PendingNote>{text("pendingWarning")}</PendingNote>}
              </Field>
            )}

            <Field>
              <FieldHeader>
                <FieldLabel>
                  {text("compraLabel")}
                  <InfoTip tabIndex={0}>
                    i<InfoBubble>{config?.tips?.compra ?? text("compraTip")}</InfoBubble>
                  </InfoTip>
                </FieldLabel>
                <FieldCaption>{text("compraCaption")}</FieldCaption>
              </FieldHeader>
              <NumberInput
                type="number"
                step={50000}
                value={compra ?? ""}
                placeholder={text("compraPlaceholder")}
                onChange={(e) => setCompra(e.target.value ? +e.target.value : null)}
              />
            </Field>
          </InputDivider>
        </InputPanel>

        <ResultPanel>
          <SectionKicker>{text("resultTitle")}</SectionKicker>

          <Waterfall>
            <WaterfallRow>
              <WaterfallLabelRow>
                <WaterfallLabel>{text("ingresosLabel")}</WaterfallLabel>
                <WaterfallValue $color="#14323b">{fmtE(ingresos)} €</WaterfallValue>
              </WaterfallLabelRow>
              <WaterfallTrack>
                <WaterfallFill $width="100%" $color="#9bc7af" />
              </WaterfallTrack>
            </WaterfallRow>

            <WaterfallRow>
              <WaterfallLabelRow>
                <WaterfallLabel>{text("construccionLabel")}</WaterfallLabel>
                <WaterfallValue>−{fmtE(cConstr)} €</WaterfallValue>
              </WaterfallLabelRow>
              <WaterfallTrack>
                <WaterfallFill $width={clampPct((cConstr / sMax) * 100)} $color="#c9820e" />
              </WaterfallTrack>
            </WaterfallRow>

            <WaterfallRow>
              <WaterfallLabelRow>
                <WaterfallLabel>{text("margenLabel")}</WaterfallLabel>
                <WaterfallValue>−{fmtE(cMargen)} €</WaterfallValue>
              </WaterfallLabelRow>
              <WaterfallTrack>
                <WaterfallFill $width={clampPct((cMargen / sMax) * 100)} $color="#d9b26a" />
              </WaterfallTrack>
            </WaterfallRow>

            {hasCargasLine && (
              <WaterfallRow>
                <WaterfallLabelRow>
                  <WaterfallLabel>{text(guaranteeFee ? "cargasAvalLineLabel" : "cargasLineLabel")}</WaterfallLabel>
                  <WaterfallValue>−{fmtE(deductions)} €</WaterfallValue>
                </WaterfallLabelRow>
                <WaterfallTrack>
                  <WaterfallFill $width={clampPct((deductions / sMax) * 100)} $color="#b4894a" />
                </WaterfallTrack>
              </WaterfallRow>
            )}

            <WaterfallRow>
              <WaterfallLabelRow>
                <WaterfallLabel $total>{text("residualLabel")}</WaterfallLabel>
                <WaterfallValue $color={Rpos ? "#14323b" : "#c0392b"}>{fmtM(Rv)}</WaterfallValue>
              </WaterfallLabelRow>
              <WaterfallTrack $height="0.625rem">
                <WaterfallFill $width={clampPct((Math.max(Rv, 0) / sMax) * 100)} $color="#35a46e" />
              </WaterfallTrack>
            </WaterfallRow>
          </Waterfall>

          <ResultStatGrid>
            <ResultStatBox $accent>
              <ResultStatLabel $accent>{text("residualStatLabel")}</ResultStatLabel>
              <ResultStatValue $color={Rpos ? "#14323b" : "#c0392b"}>{fmtM(Rv)}</ResultStatValue>
              <ResultStatSub>{fmtE(rep)} €/m²</ResultStatSub>
            </ResultStatBox>
            <ResultStatBox>
              <ResultStatLabel>{text("ofertStatLabel", { pct: taxPct.toLocaleString(i18n?.language?.startsWith("en") ? "en-GB" : "es-ES") })}</ResultStatLabel>
              <ResultStatValue>{fmtM(ofert)}</ResultStatValue>
              <ResultStatSub>{fmtE(ofert / plotArea)} €/m²</ResultStatSub>
            </ResultStatBox>
          </ResultStatGrid>

          <div>
            <ScenarioBandKicker>{text("scenarioBandTitle", bandVars)}</ScenarioBandKicker>
            <ScenarioBandGrid>
              <ScenarioBandBox>
                <ScenarioBandLabel $color="#b0704a">{text("bandLow", bandVars)}</ScenarioBandLabel>
                <ScenarioBandValue>{fmtM(Rlow)}</ScenarioBandValue>
                <ScenarioBandSub>{fmtE(Rlow / plotArea)} €/m²</ScenarioBandSub>
              </ScenarioBandBox>
              <ScenarioBandBox $central>
                <ScenarioBandLabel $color="#237a50">{text("bandCentral", bandVars)}</ScenarioBandLabel>
                <ScenarioBandValue>{fmtM(Rv)}</ScenarioBandValue>
                <ScenarioBandSub>{fmtE(rep)} €/m²</ScenarioBandSub>
              </ScenarioBandBox>
              <ScenarioBandBox>
                <ScenarioBandLabel $color="#237a50">{text("bandHigh", bandVars)}</ScenarioBandLabel>
                <ScenarioBandValue>{fmtM(Rhigh)}</ScenarioBandValue>
                <ScenarioBandSub>{fmtE(Rhigh / plotArea)} €/m²</ScenarioBandSub>
              </ScenarioBandBox>
            </ScenarioBandGrid>
          </div>

          {!Rpos && (
            <WarningBox>
              <FiAlertTriangle size={16} />
              <WarningText>{text("residualWarning")}</WarningText>
            </WarningBox>
          )}

          {hasCompra ? (
            <MarginBox $bg={colPos ? "#f4f9f6" : "#fbeee8"} $border={colPos ? "#e1eded" : "#f2d9ce"}>
              <MarginHeaderRow>
                <MarginKicker $color={colPos ? "#237a50" : "#c0392b"}>
                  {text("marginKicker")}
                </MarginKicker>
                <MarginFigures $color={colPos ? "#237a50" : "#c0392b"}>
                  <b>
                    {colPos ? "+" : "−"}
                    {fmtMn(Math.abs(colchon))}
                  </b>
                  <i>
                    {colPos ? "+" : "−"}
                    {Math.abs(colPct).toFixed(1)}%
                  </i>
                </MarginFigures>
              </MarginHeaderRow>
              <MarginVerdict>
                {colPos
                  ? text("marginVerdictPositive", {
                      value: fmtM(Math.abs(colchon)),
                      pct: Math.abs(colPct).toFixed(1),
                    })
                  : text("marginVerdictNegative", {
                      value: fmtM(Math.abs(colchon)),
                    })}
              </MarginVerdict>
              <MarginFootnote>
                {rich("marginFootnote", { value: fmtE(venBE) }, { 1: <strong /> })}
              </MarginFootnote>
            </MarginBox>
          ) : (
            <NoCompraBox>{text("noCompraText")}</NoCompraBox>
          )}
        </ResultPanel>
      </SimulatorGrid>

      <Disclaimer>{config?.disclaimer ?? text("disclaimer")}</Disclaimer>
    </SimulatorSection>
  );
};
